import { QueryClient } from '@tanstack/react-query';
import type { Order, QueueSettings, WebSocketMessage } from '../types';

// ==========================================
// Types
// ==========================================

interface QueueData {
  orders: Order[];
  settings: QueueSettings;
}

// ==========================================
// Handler Factory
// ==========================================

/**
 * Creates a WebSocket message handler that updates TanStack Query cache.
 * Uses direct cache updates for instant UI, with fallback to invalidation.
 */
export function createWebSocketHandler(queryClient: QueryClient, shopId: string) {
  return (event: MessageEvent) => {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      console.log('[Cashier WS] Received message:', message.type, message);
      handleWebSocketMessage(queryClient, shopId, message);
    } catch (err) {
      console.error('[Cashier WS] Failed to parse message:', err);
    }
  };
}

// ==========================================
// Message Handlers
// ==========================================

function handleWebSocketMessage(
  queryClient: QueryClient,
  shopId: string,
  message: WebSocketMessage
) {
  const queryKey = ['queue', shopId];
  const currentData = queryClient.getQueryData<QueueData>(queryKey);

  // Fallback: If cache is empty, invalidate to fetch fresh data
  if (!currentData) {
    console.log('[Cashier WS] Cache empty, invalidating to fetch fresh data');
    queryClient.invalidateQueries({ queryKey });
    if (message.type === 'queue:new-order') {
      playNotificationSound();
    }
    return;
  }

  // Cache exists - update directly for instant UI
  switch (message.type) {
    case 'queue:new-order':
      if (message.order) {
        queryClient.setQueryData<QueueData>(queryKey, {
          ...currentData,
          orders: [...currentData.orders, message.order],
        });
        playNotificationSound();
      }
      break;

    case 'order:called':
      if (message.orderId) {
        queryClient.setQueryData<QueueData>(queryKey, {
          ...currentData,
          orders: currentData.orders.map((order) =>
            order.id === message.orderId
              ? { ...order, status: 'called', calledAt: new Date().toISOString() }
              : order
          ),
        });
      }
      break;

    case 'order:status':
      if (message.orderId && message.status) {
        queryClient.setQueryData<QueueData>(queryKey, {
          ...currentData,
          orders: currentData.orders.map((order) =>
            order.id === message.orderId
              ? { ...order, status: message.status as Order['status'] }
              : order
          ),
        });
      }
      break;

    case 'order:cancelled':
      if (message.orderId) {
        queryClient.setQueryData<QueueData>(queryKey, {
          ...currentData,
          orders: currentData.orders.filter((order) => order.id !== message.orderId),
        });
      }
      break;

    case 'queue:settings':
      if (message.settings) {
        queryClient.setQueryData<QueueData>(queryKey, {
          ...currentData,
          settings: message.settings,
        });
      }
      break;

    case 'connected':
    case 'subscribed':
      console.log('[Cashier WS] Connection confirmed');
      // On reconnect, invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey });
      break;

    default:
      console.log('[Cashier WS] Unknown message type:', message.type);
  }
}

// ==========================================
// Utilities
// ==========================================

function playNotificationSound() {
  try {
    const audio = new Audio('/notification.mp3');
    audio.play().catch(() => {
      // Silently fail if audio can't play (user hasn't interacted yet)
    });
  } catch {
    // Silently fail
  }
}
